import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import SongTable from '../components/SongTable';
import { useAuth } from '../hooks/useAuth';

const PAGE_SIZE = 500; // Match PlatformPage page size

export default function TagsPage() {
  const { tag: tagParam } = useParams();
  const navigate = useNavigate();
  const { authToken } = useAuth();
  const [allTags, setAllTags] = useState({});
  const [relatedTags, setRelatedTags] = useState({});
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [songsLoading, setSongsLoading] = useState(false);
  const [queueLoading, setQueueLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [totalCount, setTotalCount] = useState(0);
  const offset = parseInt(searchParams.get('offset')) || 0;
  const currentPage = Math.floor(offset / PAGE_SIZE);
  
  // Parse comma-separated tag string into array
  const tags = useMemo(() => {
    return tagParam ? tagParam.split(',').filter(t => t.trim() !== '') : [];
  }, [tagParam]);
  
  // For display and API calls with a single tag
  const primaryTag = useMemo(() => tags.length > 0 ? tags[0] : null, [tags]);
  
  // Computed property for filtered songs (when using multiple tags)
  const filteredSongs = useMemo(() => {
    // Process raw songs into a cleaner format first
    const processedSongs = songs.filter(s => s && s.song).map(s => ({
      ...s.song,
      // Ensure critical fields exist to avoid errors
      id: s.song.id || 0,
      title: s.song.title || 'Unknown Title',
      length: s.song.length || 0,
      avg_vote: s.song.avg_vote || null,
      tags: s.tags || [],
      is_favorited: s.song.is_favorited || false
    }));
    
    if (tags.length <= 1) {
      return processedSongs; // If only one tag, use the API-filtered songs directly
    }
    
    // For multiple tags, filter the songs client-side to find those with all tags
    // For large datasets, this might be slow
    const allMatchingSongs = (() => {
      // Only filter if we have more than one tag
      if (tags.length <= 1) return processedSongs;
      
      const filtered = processedSongs.filter(song => {
        // Skip the first tag (already filtered by API)
        for (let i = 1; i < tags.length; i++) {
          const additionalTag = tags[i];
          // Check if song has all required tags
          if (!song.tags || !song.tags.includes(additionalTag)) {
            return false;
          }
        }
        return true;
      });
      return filtered;
    })();
    
    // When using multiple tags, apply client-side pagination to the filtered results
    if (tags.length > 1) {
      // Store the total matching songs count for reference in the component
      if (allMatchingSongs.length !== totalCount) {
        // Use setTimeout to avoid state updates during render
        setTimeout(() => setTotalCount(allMatchingSongs.length), 0);
      }
      
      // Return the paginated subset
      return allMatchingSongs.slice(offset, offset + PAGE_SIZE);
    }
    
    return allMatchingSongs;
  }, [songs, tags, offset, totalCount]);
  
  // Fetch all tags when on the main tags page
  useEffect(() => {
    setLoading(true);
    axios.get('/api/tags')
      .then(res => {
        setAllTags(res.data);
      })
      .catch(err => {
        toast.error('Failed to load tags');
      })
      .finally(() => setLoading(false));
  }, [tags.length === 0]);

  // Fetch songs by tag(s) when specific tag(s) are selected
  useEffect(() => {
    if (tags.length === 0) {
      return;
    }
    
    setSongsLoading(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // We'll use the primary tag for the API call, then filter client-side
    
    // Add auth token to request if available
    const config = authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : {};
    
    // For multiple tags, we need to fetch ALL songs for the primary tag to ensure we don't miss any intersections
    // For a single tag, we can use pagination as normal
    let paginationParams = '';
    
    if (tags.length > 1) {
      // For tag intersection, request all songs by setting a very high limit (10000)
      // and don't apply offset so we get everything from the beginning
      paginationParams = '?limit=10000';
    } else {
      // For single tag, use normal pagination
      paginationParams = `?offset=${offset}`;
    }
    
    // Start by fetching the primary tag's songs
    axios.get(`/api/tags/${encodeURIComponent(primaryTag)}${paginationParams}`, config)
      .then(res => {
        // Validate the songs structure
        const songs = res.data.songs || [];
        
        // Check data structure
        let validSongs = songs.filter(song => song && typeof song === 'object' && song.song);
        
        if (validSongs.length === 0 && songs.length > 0) {
          console.warn('Invalid song data structure received:', songs);
          toast.error('Received invalid song data format');
        } else {
          // If we have multiple tags, filter the songs by additional tags
          if (tags.length > 1) {            
            // Client-side filtering for additional tags
            validSongs = validSongs.filter(song => {
              // Skip the first tag (already filtered by API)
              for (let i = 1; i < tags.length; i++) {
                const additionalTag = tags[i];
                // Check if song has all required tags
                if (!song.tags || !song.tags.includes(additionalTag)) {
                  return false;
                }
              }
              return true;
            });
          }
        }
        
        setSongs(validSongs);
        setRelatedTags(res.data.relatedTags || {});
        
        // For a single tag, use the server-provided total count for pagination
        if (tags.length <= 1) {
          setTotalCount(res.data.total_count || 0);
        }
        // For multiple tags, the totalCount will be set in the filteredSongs useMemo
        // after we filter for all tags, so we don't need to set it here
      })
      .catch(err => {
        setSongs([]);
        setRelatedTags({});
        toast.error(`Failed to load songs for tags: ${tags.join(', ')}`);
      })
      .finally(() => setSongsLoading(false));
  }, [tags, primaryTag, offset, authToken]);

  // Queue a random song with the specified tag(s)
  const queueSong = async (options) => {
    setQueueLoading(true);
    try {
      if (!authToken) {
        toast.error('You must be logged in to queue songs');
        setQueueLoading(false);
        return;
      }
      
      if (tags.length === 0) {
        toast.error('No tags selected');
        setQueueLoading(false);
        return;
      }
      
      if (filteredSongs.length === 0) {
        toast.error('No songs available with these tags');
        setQueueLoading(false);
        return;
      }
      
      // Now we can use the backend's support for multiple tags directly
      const response = await axios.post('/api/queue', 
        {
          tags: tags, // Pass all tags as an array to use the new intersection feature
          ...options
        },
        {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }
      );
      
      if (response.data.success) {
        toast.success(`✅ Queued: ${response.data.song_details.artist} - ${response.data.song_details.title}`);
      }
    } catch (error) {
      toast.error(`❌ ${error.response?.data?.error || 'Failed to queue song'}`);
    } finally {
      setQueueLoading(false);
    }
  };

  // No longer needed since we've integrated the functionality into queueSong directly

  // Calculate font size based on tag count (for tag cloud)
  const getTagFontSize = (count, maxCount) => {
    // Min font size 0.75rem (12px), max 1.5rem (24px)
    const minSize = 0.75;
    const maxSize = 1.5;
    // Calculate relative size
    const relativeSize = count / maxCount;
    // Scale between min and max sizes
    return minSize + (maxSize - minSize) * relativeSize;
  };

  // Helper functions for pagination - match PlatformPage
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const goToPage = (pageNum) => {
    setSearchParams({ offset: pageNum * PAGE_SIZE });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // If we're on the main tags page
  if (!tagParam) {
    // Sort tags alphabetically for display
    const sortedTags = Object.entries(allTags).sort((a, b) => a[0].localeCompare(b[0]));
    const maxCount = Math.max(...Object.values(allTags));

    return (
      <div className="p-4 max-w-5xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">🏷️ Browse by Tags</h1>
        
        {loading ? (
          <div className="flex flex-wrap gap-3 py-10">
            {[...Array(30)].map((_, i) => (
              <div 
                key={i} 
                className="animate-pulse bg-gray-200 rounded-md my-1" 
                style={{ 
                  height: `${Math.floor(Math.random() * 8) + 16}px`,
                  width: `${Math.floor(Math.random() * 120) + 40}px`,
                  opacity: 0.8 - (i * 0.01)
                }}
              ></div>
            ))}
            <div className="w-full text-center text-gray-500 mt-6">Loading tags...</div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-3">
            {sortedTags.map(([tagName, count]) => (
              <span key={tagName} className="flex items-center gap-1">
                <Link
                  to={`/tags/${encodeURIComponent(tagName)}`}
                  className="text-blue-700 hover:underline transition-all hover:text-blue-900"
                  style={{ 
                    fontSize: `${getTagFontSize(count, maxCount)}rem`,
                    fontWeight: count > maxCount * 0.5 ? 'bold' : 'normal' 
                  }}
                >
                  {tagName} <span className="text-gray-500 text-xs">({count})</span>
                </Link>
                {/* Add "+" button for adding to intersection filter */}
                {tags.length > 0 && !tags.includes(tagName) && (
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      // Add this tag to the current filter
                      navigate(`/tags/${[...tags, tagName].join(',')}`);
                    }}
                    className="text-green-600 hover:text-green-800 font-bold text-xs ml-1 bg-green-100 hover:bg-green-200 rounded-full w-4 h-4 flex items-center justify-center"
                    title={`Add ${tagName} to current filter`}
                  >
                    +
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  // If we're on a specific tag page
  const maxRelatedCount = Math.max(
    ...Object.values(relatedTags).map(value => {
      // Check if we have the new data format (object) or old format (number)
      if (value && typeof value === 'object') {
        // Use the display value (raw count or percentage)
        return value.displayValue;
      }
      // Fallback to the direct value for backward compatibility
      return value;
    }), 
    0
  );

  // For tag-specific page
  return (
    <div className="p-4 max-w-5xl mx-auto">
      <div className="mb-4">
        <Link to="/tags" className="text-blue-700 hover:underline">
          ← Back to All Tags
        </Link>
      </div>
      
      <div className="flex flex-col md:flex-row md:items-start gap-6">
        <div className="flex-1">
          <h1 className="text-2xl font-bold mb-3">🏷️ {tagParam}</h1>
          
          {/* Selected Tags - Show currently active filters with option to remove */}
          {tags.length > 1 && (
            <div className="mb-6">
              <h2 className="text-lg font-medium mb-2">Active Filters:</h2>
              <div className="flex flex-wrap gap-2">
                {tags.map((selectedTag, index) => (
                  <div 
                    key={selectedTag} 
                    className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full flex items-center"
                  >
                    <span>{selectedTag}</span>
                    {/* Show X for all tags when there are multiple tags */}
                    {tags.length > 1 && (
                      <button 
                        onClick={() => {
                          const newTags = [...tags];
                          newTags.splice(index, 1);
                          // If removing the first tag, navigate to the second tag as primary
                          if (index === 0 && newTags.length > 0) {
                            navigate(`/tags/${newTags.join(',')}`);
                          } else {
                            // Otherwise just remove the selected tag
                            navigate(`/tags/${newTags.join(',')}`);
                          }
                        }}
                        className="ml-2 text-blue-600 hover:text-blue-800 font-bold"
                        title={`Remove ${selectedTag} from filter`}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Showing songs that have <strong>all</strong> of these tags (intersection).
              </p>
            </div>
          )}
          
          <div className="mb-6 flex items-center">
            <span className="text-gray-600">
              {filteredSongs.length === 0 && !songsLoading ? (
                "No songs found with these tags"
              ) : tags.length > 1 ? (
                <>
                  Showing {Math.min(offset + 1, totalCount)}-{Math.min(offset + PAGE_SIZE, totalCount)} of {totalCount} song{totalCount !== 1 ? "s" : ""} matching all tags
                </>
              ) : (
                <>
                  {filteredSongs.length} song{filteredSongs.length !== 1 ? "s" : ""}
                  {totalCount > 0 && totalCount !== filteredSongs.length && (
                    <> (of {totalCount} total)</>
                  )}
                </>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Related Tags */}
      <div className="mb-6">
        <h2 className="text-lg font-bold mb-2">Related Tags:</h2>
        {loading ? (
          <div className="py-2 flex flex-wrap gap-2">
            {[...Array(8)].map((_, i) => (
              <div 
                key={i} 
                className="animate-pulse bg-gray-200 h-6 rounded-md" 
                style={{ 
                  width: `${Math.floor(Math.random() * 60) + 40}px`,
                  opacity: 1 - (i * 0.08) // Gradually fade out for visual effect
                }}
              ></div>
            ))}
          </div>
        ) : Object.keys(relatedTags).length > 0 ? (
          <div className="flex flex-wrap gap-3">
            {Object.entries(relatedTags)
              .sort((a, b) => a[0].localeCompare(b[0]))
              .map(([relatedTag, tagData]) => {
                // Handle both old and new data format
                const isNewFormat = tagData && typeof tagData === 'object';
                const displayValue = isNewFormat ? tagData.displayValue : tagData;
                const useRawCounts = isNewFormat ? tagData.useRawCounts : false;
                const percentage = isNewFormat ? tagData.percentage : tagData;
                
                // Determine font size and style based on value
                const fontSizeValue = isNewFormat ? 
                  (useRawCounts ? tagData.count : tagData.percentage) : 
                  tagData;
                
                return (
                  <span key={relatedTag} className="flex items-center gap-1">
                    <Link
                      to={`/tags/${encodeURIComponent(relatedTag)}`}
                      className="text-blue-700 hover:underline transition-all hover:text-blue-900"
                      style={{ 
                        fontSize: `${getTagFontSize(fontSizeValue, maxRelatedCount)}rem`,
                        fontWeight: fontSizeValue > maxRelatedCount * 0.7 ? 'bold' : 'normal' 
                      }}
                    >
                      {relatedTag} <span className="text-gray-500 text-xs">
                        {/* Display differently based on format */}
                        {isNewFormat ? (
                          useRawCounts ? 
                            `(${tagData.count})` : 
                            `(${percentage}%)`
                        ) : (
                          `(${displayValue})`
                        )}
                      </span>
                    </Link>
                    {/* Add "+" button for adding to intersection filter */}
                    {tags.length > 0 && !tags.includes(relatedTag) && (
                      <button 
                        onClick={(e) => {
                          e.preventDefault();
                          // Add this tag to the current filter
                          navigate(`/tags/${[...tags, relatedTag].join(',')}`);
                        }}
                        className="text-green-600 hover:text-green-800 font-bold text-xs ml-1 bg-green-100 hover:bg-green-200 rounded-full w-4 h-4 flex items-center justify-center"
                        title={`Add ${relatedTag} to current filter`}
                      >
                        +
                      </button>
                    )}
                  </span>
                );
              })}
          </div>
        ) : (
          <p className="text-gray-500">No related tags found</p>
        )}
      </div>

      {/* Queue buttons */}
      <div className="mb-6">
        <h2 className="text-lg font-bold mb-2">Queue Random Song:</h2>
        {loading || songsLoading ? (
          <div className="flex flex-wrap gap-2">
            {[...Array(4)].map((_, i) => (
              <div 
                key={i}
                className="bg-gray-200 animate-pulse h-10 w-32 rounded"
              ></div>
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            <button 
              onClick={() => queueSong({ least_recent: true })}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded disabled:opacity-50"
              disabled={queueLoading || !authToken || filteredSongs.length === 0}
              title={!authToken ? "Login required" : filteredSongs.length === 0 ? "No songs available with these tags" : ""}
            >
              {queueLoading ? 'Queueing...' : 'Least Recent'}
            </button>
            <button 
              onClick={() => queueSong({})}
              className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded disabled:opacity-50"
              disabled={queueLoading || !authToken || filteredSongs.length === 0}
              title={!authToken ? "Login required" : filteredSongs.length === 0 ? "No songs available with these tags" : ""}
            >
              {queueLoading ? 'Queueing...' : 'Random'}
            </button>
            <button 
              onClick={() => queueSong({ min_vote: 3 })}
              className="bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-2 px-4 rounded disabled:opacity-50"
              disabled={queueLoading || !authToken || filteredSongs.length === 0}
              title={!authToken ? "Login required" : filteredSongs.length === 0 ? "No songs available with these tags" : ""}
            >
              {queueLoading ? 'Queueing...' : 'Random 3⭐+'}
            </button>
            <button 
              onClick={() => queueSong({ min_vote: 4 })}
              className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded disabled:opacity-50"
              disabled={queueLoading || !authToken || filteredSongs.length === 0}
              title={!authToken ? "Login required" : filteredSongs.length === 0 ? "No songs available with these tags" : ""}
            >
              {queueLoading ? 'Queueing...' : 'Random 4⭐+'}
            </button>
          </div>
        )}
      </div>

      {/* Songs table */}
      <div>
        <h2 className="text-lg font-bold mb-2">
          {tags.length > 1 ? (
            <>Songs with all selected tags:</>
          ) : (
            <>Songs with tag "{primaryTag}":</>
          )}
          {totalCount > 0 && (
            <span className="text-sm font-normal ml-2 text-gray-500">
              (showing {Math.min(offset + 1, totalCount)}-{Math.min(offset + (tags.length > 1 ? filteredSongs.length : songs.length), totalCount)} of {totalCount})
            </span>
          )}
        </h2>

        {/* Page buttons (top) - match PlatformPage */}
        {totalCount > PAGE_SIZE && (
          <div className="mb-4 flex justify-center gap-2 flex-wrap">
            <button
              disabled={offset === 0}
              onClick={() => setSearchParams({ offset: offset - PAGE_SIZE })}
              className="text-blue-600 hover:underline disabled:opacity-40"
            >
              ← Previous
            </button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => goToPage(i)}
                className={`px-2 py-1 rounded text-sm ${
                  i === currentPage
                    ? 'bg-blue-700 text-white font-bold'
                    : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              disabled={(tags.length > 1 ? offset + PAGE_SIZE >= totalCount : songs.length < PAGE_SIZE)}
              onClick={() => setSearchParams({ offset: offset + PAGE_SIZE })}
              className="text-blue-600 hover:underline disabled:opacity-40"
            >
              Next →
            </button>
          </div>
        )}
        {loading ? (
          <div className="text-center py-10">
            <div className="flex flex-col items-center">
              <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
              <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
              <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
              <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
              <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
              <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
              <div className="animate-pulse bg-gray-200 h-8 w-3/4 mb-2 rounded"></div>
              <div className="text-gray-500 mt-2">Loading songs...</div>
            </div>
          </div>
        ) : songs.length > 0 ? (
          <>
            {/* Debug information */}
            {process.env.NODE_ENV === 'development' && (
              <div className="text-xs text-gray-500 mb-2">
                <div>Found {songs.length} songs with primary tag {primaryTag}</div>
                <div>
                  {tags.length > 1 ? 
                    `Filtered to ${totalCount} songs matching all ${tags.length} tags` :
                    `Total: ${totalCount} songs`}
                </div>
                <div>Displayed: {filteredSongs.length} items (page {currentPage + 1} of {totalPages || 1})</div>
                <div>Pagination: {tags.length > 1 ? 'Client-side with limit=10000' : 'Server-side with default limit=500'}</div>
                <div>Related tags format: {Object.values(relatedTags)[0] && typeof Object.values(relatedTags)[0] === 'object' ? 'New format with metadata' : 'Legacy format'}</div>
                {Object.values(relatedTags).length > 0 && Object.values(relatedTags)[0] && typeof Object.values(relatedTags)[0] === 'object' && (
                  <div>Showing related tags as: {Object.values(relatedTags)[0].useRawCounts ? 'Raw counts' : 'Percentages'}</div>
                )}
              </div>
            )}
            {/* Use error boundary pattern */}
            {(() => {
              try {
                const processedSongs = songs
                  .filter(s => s && s.song)
                  .map(s => {
                    return {
                      ...s.song,
                      // Ensure critical fields exist to avoid errors
                      id: s.song.id || 0,
                      title: s.song.title || 'Unknown Title',
                      length: s.song.length || 0,
                      avg_vote: s.song.avg_vote || null,
                      is_favorited: s.song.is_favorited || false
                    };
                  });
                
                if (processedSongs.length === 0) {
                  return <p className="text-gray-500">No valid songs found with this tag</p>;
                }
                
                return (
                  <>
                    <SongTable 
                      songs={filteredSongs}
                      showTime={true} 
                      showFavorites={true} 
                      showRequest={true}
                      disableRequestIf={() => false}
                      sortable={true}
                    />
                    
                    {/* Pagination controls - match PlatformPage */}
                    {totalCount > PAGE_SIZE && (
                      <div className="flex justify-center gap-2 flex-wrap mt-6 mb-4">
                        <button
                          disabled={offset === 0}
                          onClick={() => setSearchParams({ offset: offset - PAGE_SIZE })}
                          className="text-blue-600 hover:underline disabled:opacity-40"
                        >
                          ← Previous
                        </button>
                        {Array.from({ length: totalPages }).map((_, i) => (
                          <button
                            key={i}
                            onClick={() => goToPage(i)}
                            className={`px-2 py-1 rounded text-sm ${
                              i === currentPage
                                ? 'bg-blue-700 text-white font-bold'
                                : 'bg-gray-200 hover:bg-gray-300'
                            }`}
                          >
                            {i + 1}
                          </button>
                        ))}
                        <button
                          disabled={(tags.length > 1 ? offset + PAGE_SIZE >= totalCount : songs.length < PAGE_SIZE)}
                          onClick={() => setSearchParams({ offset: offset + PAGE_SIZE })}
                          className="text-blue-600 hover:underline disabled:opacity-40"
                        >
                          Next →
                        </button>
                      </div>
                    )}
                  </>
                );
              } catch (err) {
                return <p className="text-red-500">Error displaying songs: {err.message}</p>;
              }
            })()}
          </>
        ) : (
          <p className="text-gray-500">No songs found with this tag</p>
        )}
      </div>
    </div>
  );
}